import { useEffect, useRef, useState } from 'react';

/** How far below the viewport the sentinel may sit and still request the next page, so rows arrive before the end. */
export const INFINITE_SCROLL_ROOT_MARGIN = '200px';

export type UseInfiniteScrollOptions = {
  /** `true` arms the sentinel. `false` or `undefined` leaves it inert: the caller has not said there is more. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /**
   * A failed next-page fetch leaves `hasNextPage` true, because it derives from the last successful page. Without
   * this flag the sentinel would retry the failing endpoint on every settle. See #8453.
   */
  isError?: boolean;
  onLoadMore?: () => void;
  /**
   * Re-arms the sentinel when it changes, for a load that adds rows without a fetch (revealing cached pages).
   * Nothing else tells the hook that content arrived in that case. Callers pass something that changes with the
   * rows, such as their count.
   */
  resetKey?: unknown;
  rootMargin?: string;
};

/**
 * Requests the next page when a sentinel element scrolls into view.
 *
 * Returns a callback ref to attach to the sentinel. It observes only while there is a next page, no fetch is in
 * flight and the last fetch did not fail. It fires once per entry into view: after firing it waits for the
 * sentinel to leave the viewport, for the fetch to settle, or for `resetKey` to change before it can fire again.
 * When a fetch settles and the sentinel is still in view it fires again, which chains pages until the sentinel
 * leaves the viewport.
 *
 * The root is the viewport. Intersection accounts for ancestor clipping, so the sentinel works inside a
 * scrolling container as well as on a page that scrolls as a whole.
 */
export const useInfiniteScroll = ({
  hasNextPage,
  isFetchingNextPage,
  isError,
  onLoadMore,
  resetKey,
  rootMargin = INFINITE_SCROLL_ROOT_MARGIN,
}: UseInfiniteScrollOptions): ((node: Element | null) => void) => {
  // State rather than a ref, so the observer is (re)created when the sentinel mounts, not only when the flags change.
  const [sentinel, setSentinel] = useState<Element | null>(null);
  // The latest callback, so an inline arrow from the caller does not reconnect the observer on every render.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const armed = hasNextPage === true && !isFetchingNextPage && !isError && onLoadMore !== undefined;

  useEffect(() => {
    if (!armed || !sentinel) return undefined;
    // An entry queued before `disconnect` can still be delivered after cleanup; `active` keeps it from firing.
    let active = true;
    // One request per entry into view: set on firing, cleared once the sentinel has left the viewport.
    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!active) return;
        const isIntersecting = entries.some((entry) => entry.isIntersecting);
        if (!isIntersecting) {
          fired = false;
          return;
        }
        if (fired) return;
        fired = true;
        onLoadMoreRef.current?.();
      },
      { rootMargin, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [armed, sentinel, rootMargin, resetKey]);

  return setSentinel;
};
